import { Fragment, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { PageHeader } from "@/components/portal/PageHeader"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import "./angebote.css"
import {
  angeboteApi, euro, HERKUNFT_TEXT,
  type AngebotEntwurf, type AngebotPosition, type HeroProjekt,
} from "@/lib/angebote"

/**
 * Angebotsentwurf (Paul 08.10.2026): ganze Tabelle, Herkunft je Position,
 * Bearbeiten per Anweisung („Wanne statt Dusche") oder direkt in der Tabelle,
 * dann „In HERO übernehmen". Preise setzt nie die KI — Katalog, frühere
 * Angebote oder der Inhaber.
 */

type Zeile = AngebotPosition & { _neu?: boolean }

const BEISPIELE = ["Stunden um 20 % erhöhen", "Günstigere Armaturen", "Badheizkörper dazu", "Demontage und Entsorgung ergänzen"]

const HERKUNFT_FARBE: Record<string, string> = {
  katalog: "pp-badge-booked", historie: "pp-badge-done", neu: "pp-badge-ringing", manuell: "pp-badge-answered",
}

function zahl(v: string): number | null {
  const t = v.replace(/\./g, "").replace(",", ".").trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) && n >= 0 ? n : null
}

function zahlText(n: number | null | undefined): string {
  return n === null || n === undefined ? "" : String(n).replace(".", ",")
}

function ProjektWahl({ tenant, entwurf, onGewaehlt }: {
  tenant: string; entwurf: AngebotEntwurf; onGewaehlt: (p: HeroProjekt | null) => void
}) {
  const [suche, setSuche] = useState("")
  const [aktiv, setAktiv] = useState(suche.length >= 2)
  const q = useQuery({
    queryKey: ["angebot-projekte", tenant, suche],
    queryFn: () => angeboteApi.projekteSuchen(tenant, suche),
    enabled: aktiv && suche.trim().length >= 2,
  })
  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600 }}>HERO-Projekt:</span>
        {entwurf.project_match_id ? (
          <>
            <span>{entwurf.projekt_name || `Projekt ${entwurf.project_match_id}`}</span>
            {entwurf.hero_link && <a href={entwurf.hero_link} target="_blank" rel="noreferrer" data-frei>in HERO öffnen ↗</a>}
            <button type="button" className="pp-btn pp-btn-sm" onClick={() => onGewaehlt(null)}>ändern</button>
          </>
        ) : (
          <>
            <input value={suche} onChange={(e) => { setSuche(e.target.value); setAktiv(true) }}
                   placeholder="Kunde, Projektnummer oder Ort" style={{ minWidth: 220 }}
                   data-testid="angebot-projekt-suche" />
            {q.isFetching && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>suche …</span>}
          </>
        )}
      </div>
      {!entwurf.project_match_id && (q.data?.projekte ?? []).length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0", display: "flex", flexDirection: "column", gap: 4 }}>
          {q.data!.projekte.map((p) => (
            <li key={p.id}>
              <button type="button" className="pp-btn pp-btn-sm" onClick={() => { onGewaehlt(p); setAktiv(false) }}>
                {[p.nr, p.name, p.kunde, p.ort].filter(Boolean).join(" · ")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default function AngebotDetail() {
  const { id } = useParams()
  const entwurfId = Number(id)
  const [tenant] = useTenantFilter()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const key = ["angebot", tenant, entwurfId]

  const q = useQuery({
    queryKey: key,
    queryFn: () => angeboteApi.holen(tenant, entwurfId),
    enabled: !!tenant && Number.isFinite(entwurfId),
  })

  const [zeilen, setZeilen] = useState<Zeile[]>([])
  const [titel, setTitel] = useState("")
  const [anweisung, setAnweisung] = useState("")
  const [geaendert, setGeaendert] = useState(false)
  // Neuer Stand vom Server → Tabelle neu fuellen (waehrend des Renderns statt
  // im Effekt, siehe react.dev „you might not need an effect").
  const [stand, setStand] = useState<string | null>(null)
  if (q.data && q.data.updated_at !== stand) {
    setStand(q.data.updated_at)
    setZeilen(q.data.positionen)
    setTitel(q.data.titel)
    setGeaendert(false)
  }

  const setzeDaten = (e: AngebotEntwurf) => {
    qc.setQueryData(key, (alt: AngebotEntwurf | undefined) => ({ ...alt, ...e }))
    qc.invalidateQueries({ queryKey: ["angebote", tenant] })
  }
  const fehler = (e: unknown) => toast(e instanceof Error ? e.message : String(e), "error")

  const speichern = useMutation({
    mutationFn: () => angeboteApi.aendern(tenant, entwurfId, {
      titel,
      positionen: zeilen.map((z) => ({ id: z.id, text: z.text, menge: z.menge, einheit: z.einheit || "Stk", preis: z.preis, wahl: !!z.wahl, abschnitt: z.abschnitt ?? "" })),
    }),
    onSuccess: (e) => { setzeDaten(e); toast("✓ Gespeichert") },
    onError: fehler,
  })
  const ki = useMutation({
    mutationFn: (text?: string) => angeboteApi.anweisung(tenant, entwurfId, text ?? anweisung),
    onSuccess: (e) => { setzeDaten(e); setAnweisung(""); toast("✓ Entwurf angepasst") },
    onError: fehler,
  })
  const projekt = useMutation({
    mutationFn: (p: HeroProjekt | null) => angeboteApi.aendern(tenant, entwurfId, {
      project_match_id: p ? p.id : 0,
      projekt_name: p ? [p.nr, p.name].filter(Boolean).join(" · ") : null,
    }),
    onSuccess: setzeDaten,
    onError: fehler,
  })
  const vorlage = useMutation({
    mutationFn: (heroDocId: number) => angeboteApi.vorlageUebernehmen(tenant, entwurfId, heroDocId),
    onSuccess: (e) => { setzeDaten(e); toast("✓ Früheres Angebot übernommen – Preise auf heutigem Stand") },
    onError: fehler,
  })
  const antwort = useMutation({
    mutationFn: (w: { frage: number; antwort: number }) => angeboteApi.antwort(tenant, entwurfId, w.frage, w.antwort),
    onSuccess: setzeDaten,
    onError: fehler,
  })
  const [offeneVarianten, setOffeneVarianten] = useState<string | null>(null)
  const variante = useMutation({
    mutationFn: (w: { uid: string; nr: number }) => angeboteApi.variante(tenant, entwurfId, w.uid, w.nr),
    onSuccess: (e) => { setzeDaten(e); setOffeneVarianten(null) },
    onError: fehler,
  })
  const aufnehmen = useMutation({
    mutationFn: (nr: number) => angeboteApi.vorschlagAufnehmen(tenant, entwurfId, nr),
    onSuccess: (e) => { setzeDaten(e); toast("✓ Position aufgenommen") },
    onError: fehler,
  })
  const hero = useMutation({
    mutationFn: () => angeboteApi.nachHero(tenant, entwurfId),
    onSuccess: (e) => { setzeDaten(e); toast("✓ Als Angebotsentwurf in HERO angelegt") },
    onError: fehler,
  })
  const weg = useMutation({
    mutationFn: () => angeboteApi.loeschen(tenant, entwurfId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["angebote", tenant] }); navigate(`/angebote?tenant=${encodeURIComponent(tenant)}`) },
    onError: fehler,
  })

  const summe = useMemo(() => zeilen.reduce((s, z) => s + (z.wahl ? 0 : (z.preis ?? 0) * (z.menge ?? 0)), 0), [zeilen])
  const ohnePreis = zeilen.filter((z) => z.preis === null && z.herkunft !== "katalog").length
  const alternativSumme = zeilen.reduce((s, z) => s + (z.wahl ? (z.preis ?? 0) * (z.menge ?? 0) : 0), 0)
  const stunden = zeilen.reduce((s, z) => s + (!z.wahl && /^(std|h|stunden?)\.?$/i.test((z.einheit || "").trim()) ? (z.menge ?? 0) : 0), 0)

  const aendere = (i: number, teil: Partial<Zeile>) => {
    setZeilen((zs) => zs.map((z, j) => (j === i ? { ...z, ...teil } : z)))
    setGeaendert(true)
  }
  const entferne = (i: number) => { setZeilen((zs) => zs.filter((_, j) => j !== i)); setGeaendert(true) }
  // Abschnitte („Gäste-WC“, „Wärmepumpe“, „Sonstiges“): in HERO wird jeder eine Gruppe mit Überschrift.
  const gegliedert = new Set(zeilen.map((z) => z.abschnitt ?? "")).size > 1
  const abschnittUmbenennen = (alt: string, neu: string) => {
    setZeilen((zs) => zs.map((z) => ((z.abschnitt ?? "") === alt ? { ...z, abschnitt: neu } : z)))
    setGeaendert(true)
  }
  const abschnittSumme = (name: string) =>
    zeilen.reduce((s, z) => s + ((z.abschnitt ?? "") === name && !z.wahl ? (z.preis ?? 0) * (z.menge ?? 0) : 0), 0)
  const hinzu = () => {
    setZeilen((zs) => [...zs, { id: `neu${zs.length + 1}`, text: "", menge: 1, einheit: "Stk", preis: null, herkunft: "neu", _neu: true,
                                abschnitt: zs.length ? zs[zs.length - 1].abschnitt ?? "" : "" }])
    setGeaendert(true)
  }

  if (!tenant) return <div className="pp-card" style={{ padding: 32 }}>Bitte Betrieb wählen.</div>
  if (q.isLoading) return <Loading label="Lade Angebotsentwurf…" />
  if (q.isError) return <ErrorBox error={q.error} />
  const e = q.data!
  const beschaeftigt = speichern.isPending || ki.isPending || hero.isPending || vorlage.isPending || aufnehmen.isPending || antwort.isPending || variante.isPending

  return (
    <>
      <PageHeader title="Angebotsentwurf" />
      <div className="ang">
        <Link className="ang-zurueck" to={`/angebote?tenant=${encodeURIComponent(tenant)}`} data-frei>← alle Entwürfe</Link>

        <section className="pp-card ang-kopf">
          <div className="ang-kopf-zeile">
            <input className="ang-titel" value={titel} aria-label="Titel"
                   onChange={(ev) => { setTitel(ev.target.value); setGeaendert(true) }} />
            {e.status === "in_hero"
              ? <span className="pp-badge pp-badge-booked" data-frei>in HERO</span>
              : <span className="pp-badge pp-badge-done" data-frei>Entwurf</span>}
          </div>
          <div className="ang-kennzahlen" data-frei>
            <div className="ang-kennzahl ist-haupt"><span>Summe netto</span><strong>{euro(summe)}</strong></div>
            <div className="ang-kennzahl"><span>inkl. 19 % MwSt.</span><strong>{euro(summe * 1.19)}</strong></div>
            <div className="ang-kennzahl"><span>Positionen</span><strong>{zeilen.length}</strong></div>
            <div className="ang-kennzahl"><span>Arbeitszeit</span><strong>{zahlText(Math.round(stunden * 10) / 10) || "0"} Std</strong></div>
          </div>
          <p className="ang-auftrag">{e.beschreibung}</p>
          <ProjektWahl tenant={tenant} entwurf={e} onGewaehlt={(p) => projekt.mutate(p)} />
          {e.rueckfragen.length > 0 && (
            <div className="ang-rueckfragen">
              <strong>Rückfragen der KI</strong>
              {e.rueckfragen.map((r, i) => {
                const frage = typeof r === "string" ? r : r.frage
                const antworten = typeof r === "string" ? [] : r.antworten
                return (
                  <div key={i} className="ang-rueckfrage">
                    <span>{frage}</span>
                    {antworten.length > 0 && (
                      <span className="ang-chips" style={{ marginTop: 4 }}>
                        {antworten.map((a, nr) => {
                          // Neu: die Antwort bringt ihre Zeilen mit → Tausch sofort, ohne KI.
                          const direkt = typeof a !== "string"
                          const text = direkt ? a.text : a
                          return (
                            <button key={text} type="button" className="ang-chip ang-chip-antwort" disabled={beschaeftigt || geaendert}
                                    title={geaendert ? "Erst die Änderungen speichern"
                                      : direkt && a.annahme ? "So steht es schon im Entwurf – Frage erledigen"
                                      : "Entwurf mit dieser Antwort anpassen"}
                                    onClick={() => (direkt ? antwort.mutate({ frage: i, antwort: nr })
                                      : ki.mutate(`Zur Rückfrage „${frage}“: ${text}`))}>
                              {text}{direkt && a.annahme ? " ✓" : ""}</button>
                          )
                        })}
                      </span>
                    )}
                  </div>
                )
              })}
              {ki.isPending && <div className="ang-klein" style={{ marginTop: 6 }}>KI passt den Entwurf an …</div>}
            </div>
          )}
        </section>

        {e.vorlagen.length > 0 && (
          <details className="pp-card ang-grundlage" data-testid="angebot-grundlage" open={e.vorlagen.length <= 3}>
            <summary>Grundlage: {e.vorlagen.length} ähnliche frühere Angebote</summary>
            <div className="ang-klein" style={{ margin: "6px 0 8px" }}>
              Der Entwurf beruht vor allem auf dem Angebot mit der Marke „Basis“. Passt eines davon genau, kannst du es
              1:1 übernehmen – alle Positionen und Mengen, Preise auf heutigem Stand.
            </div>
            {e.vorlagen.map((v) => (
              <div key={v.hero_doc_id} className="ang-vorlage">
                <span className="ang-vorlage-text">
                  {v.datum ? new Date(v.datum).toLocaleDateString("de-DE") : "ohne Datum"} · {v.projekttyp || "ohne Projekttyp"}
                  {v.anzahl ? ` · ${v.anzahl} Positionen` : ""}
                  {v.abschnitte && v.abschnitte.length > 0 ? ` · genutzt: ${v.abschnitte.join(", ")}` : ""}
                  {v.basis && <span className="pp-badge pp-badge-new" style={{ marginLeft: 6 }}>Basis</span>}
                </span>
                <span className="ang-entwurf-summe">{v.wert_netto != null ? euro(v.wert_netto) : "–"}</span>
                {v.hero_link ? <a href={v.hero_link} target="_blank" rel="noreferrer" data-frei>Projekt in HERO ↗</a> : <span />}
                <button type="button" className="pp-btn pp-btn-sm" disabled={beschaeftigt || geaendert}
                        aria-label={`Angebot vom ${v.datum || "unbekannt"} 1:1 übernehmen`}
                        onClick={() => { if (window.confirm("Die Positionen des Entwurfs durch dieses frühere Angebot ersetzen?")) vorlage.mutate(v.hero_doc_id) }}>
                  1:1 übernehmen
                </button>
              </div>
            ))}
          </details>
        )}

        <section className="ang-ki">
          <label htmlFor="angebot-anweisung" className="ang-ki-titel">✨ Mit KI ändern</label>
          <div className="ang-ki-zeile">
            <input id="angebot-anweisung" data-testid="angebot-anweisung" value={anweisung}
                   onChange={(ev) => setAnweisung(ev.target.value)}
                   onKeyDown={(ev) => { if (ev.key === "Enter" && anweisung.trim().length >= 2 && !beschaeftigt && !geaendert) ki.mutate(undefined) }}
                   placeholder="z. B. Wanne statt Dusche · Stunden auf 60 · Badheizkörper dazu" />
            <button type="button" className="pp-btn pp-btn-primary" data-testid="angebot-anweisung-senden"
                    disabled={anweisung.trim().length < 2 || beschaeftigt || geaendert}
                    onClick={() => ki.mutate(undefined)}>
              {ki.isPending ? "KI arbeitet …" : "Ausführen"}
            </button>
          </div>
          <div className="ang-chips">
            {BEISPIELE.map((b) => (
              <button key={b} type="button" className="ang-chip" onClick={() => setAnweisung(b)}>{b}</button>
            ))}
          </div>
          {geaendert && <div className="ang-klein" style={{ marginTop: 6 }}>
            Erst die Änderungen in der Tabelle speichern, dann mit KI weiterarbeiten.</div>}
        </section>

        <section className="pp-card ang-liste" data-testid="angebot-tabelle">
          <div className="ang-spalten" aria-hidden="true">
            <span>#</span><span>Position</span><span>Menge</span><span>Einheit</span><span>Preis netto</span>
            <span style={{ textAlign: "right" }}>Gesamt</span><span />
          </div>
          {zeilen.map((z, i) => {
            const fehlt = z.preis === null && z.herkunft !== "katalog"
            const gesamt = (z.preis ?? 0) * (z.menge ?? 0)
            const abschnitt = z.abschnitt ?? ""
            const neuerAbschnitt = gegliedert && (i === 0 || (zeilen[i - 1].abschnitt ?? "") !== abschnitt)
            return (
              <Fragment key={z.id + i}>
              {neuerAbschnitt && (
                <div className="ang-abschnitt" data-testid="angebot-abschnitt">
                  <input value={abschnitt} placeholder="Abschnitt ohne Überschrift" aria-label={`Überschrift Abschnitt ${abschnitt || "ohne Namen"}`}
                         onChange={(ev) => abschnittUmbenennen(abschnitt, ev.target.value)} />
                  <strong data-frei>{euro(abschnittSumme(abschnitt))}</strong>
                </div>
              )}
              <div className={`ang-pos${z.wahl ? " ist-wahl" : ""}${fehlt ? " ohne-preis" : ""}`}>
                <div className="ang-nr">{i + 1}</div>
                <div className="ang-text">
                  <textarea value={z.text} rows={Math.min(4, Math.max(1, Math.ceil(z.text.length / 60)))}
                            aria-label={`Text ${i + 1}`}
                            onChange={(ev) => aendere(i, { text: ev.target.value })} />
                  <div className="ang-meta">
                    <span data-frei className={`pp-badge ${HERKUNFT_FARBE[z.herkunft] ?? ""}`}>
                      {HERKUNFT_TEXT[z.herkunft] ?? z.herkunft}{!z.vorlagen ? "" : z.vorlagen === "Basis" ? " · aus der Basis-Vorlage"
                        : z.vorlagen === "Vorlage" ? " · aus einer Vorlage" : ` · in ${z.vorlagen} Vorlagen`}
                    </span>
                    <label className={z.wahl ? "ist-an" : ""}>
                      <input type="checkbox" checked={!!z.wahl} aria-label={`Position ${i + 1} als Alternative`}
                             onChange={(ev) => aendere(i, { wahl: ev.target.checked })} />
                      {z.wahl ? "Alternative – zählt nicht zur Summe" : "Alternative"}
                    </label>
                    {z.uid && (z.varianten ?? []).length > 0 && (
                      <button type="button" className="ang-chip ang-chip-variante" aria-expanded={offeneVarianten === z.uid}
                              aria-label={`Varianten zu Position ${i + 1}`}
                              onClick={() => setOffeneVarianten(offeneVarianten === z.uid ? null : z.uid!)}>
                        Variante · {z.varianten!.length} weitere {offeneVarianten === z.uid ? "▴" : "▾"}
                      </button>
                    )}
                    {z.hinweis && <span style={{ color: z.unsicher ? "var(--warning-text)" : "var(--text-muted)" }}>
                      {z.unsicher ? "⚠ " : ""}{z.hinweis}</span>}
                    {z.preis_frueher != null && (z.herkunft === "katalog" || z.herkunft === "historie") && (
                      <span className="ang-klein">{z.herkunft === "katalog" ? "aktueller Stammpreis" : "jüngster Preis"} · in der Vorlage {euro(z.preis_frueher)}</span>)}
                    {z.preis_spanne && z.preis_spanne[0] !== null && z.preis_spanne[1] !== null && z.preis_spanne[0] !== z.preis_spanne[1] && (
                      <span className="ang-klein">früher {euro(z.preis_spanne[0])}–{euro(z.preis_spanne[1])}</span>)}
                  </div>
                </div>
                {z.uid && offeneVarianten === z.uid && (z.varianten ?? []).length > 0 && (
                  <div className="ang-varianten" data-testid="angebot-varianten">
                    <div className="ang-klein">Andere Varianten dieses Teils aus früheren Angeboten – ein Klick tauscht die Zeile:</div>
                    {z.varianten!.map((v, nr) => (
                      <div key={v.uid ?? nr} className="ang-vorlage">
                        <span className="ang-vorlage-text">{v.text}{v.angeboten ? ` · ${v.angeboten}× angeboten` : ""}</span>
                        <span className="ang-entwurf-summe">{v.preis != null ? euro(v.preis) : "–"}</span>
                        <span />
                        <button type="button" className="pp-btn pp-btn-sm" disabled={beschaeftigt || geaendert}
                                title={geaendert ? "Erst die Änderungen speichern" : undefined}
                                aria-label={`Variante ${nr + 1} zu Position ${i + 1} nehmen`}
                                onClick={() => variante.mutate({ uid: z.uid!, nr })}>nehmen</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="ang-feld ang-menge"><span>Menge</span>
                  <input inputMode="decimal" value={zahlText(z.menge)} aria-label={`Menge ${i + 1}`}
                         onChange={(ev) => aendere(i, { menge: zahl(ev.target.value) })} /></div>
                <div className="ang-feld ang-einheit"><span>Einheit</span>
                  <input value={z.einheit} aria-label={`Einheit ${i + 1}`}
                         onChange={(ev) => aendere(i, { einheit: ev.target.value })} /></div>
                <div className="ang-feld ang-preis"><span>Preis netto</span>
                  {z.herkunft === "katalog" && z.preis === null
                    ? <div className="ang-klein" style={{ paddingTop: 7 }}>aus HERO</div>
                    : <input inputMode="decimal" value={zahlText(z.preis)} aria-label={`Preis ${i + 1}`}
                             placeholder="fehlt"
                             onChange={(ev) => aendere(i, { preis: zahl(ev.target.value) })}
                             style={{ borderColor: z.preis === null ? "var(--warning-color)" : undefined }} />}
                </div>
                <div className="ang-gesamt">
                  {z.preis === null ? "–" : z.wahl ? `(${euro(gesamt)})` : euro(gesamt)}</div>
                <button type="button" className="ang-weg" title="Position entfernen"
                        aria-label={`Position ${i + 1} entfernen`} onClick={() => entferne(i)}>✕</button>
              </div>
              </Fragment>
            )
          })}
          <div className="ang-liste-fuss">
            <button type="button" className="pp-btn pp-btn-sm" onClick={hinzu}>+ Position</button>
          </div>
        </section>

        {(e.vorschlaege ?? []).length > 0 && (
          <section className="pp-card ang-grundlage" data-testid="angebot-vorschlaege">
            <strong>Nicht übernommen: {e.vorschlaege!.length} Position(en) aus der Vorlage</strong>
            <div className="ang-klein" style={{ margin: "4px 0 8px" }}>
              Diese Zeilen standen nur in einem früheren Angebot und sind im Auftrag nicht genannt – vermutlich ein
              Sonderwunsch des damaligen Kunden. Wenn sie doch dazugehören, nimm sie mit einem Klick auf.
            </div>
            {e.vorschlaege!.map((v, nr) => (
              <div key={nr} className="ang-vorlage">
                <span className="ang-vorlage-text">{zahlText(v.menge)} {v.einheit} · {v.text}{v.abschnitt ? ` · ${v.abschnitt}` : ""}</span>
                <span className="ang-entwurf-summe">{v.preis != null ? euro(v.preis * (v.menge ?? 0)) : "–"}</span>
                <span />
                <button type="button" className="pp-btn pp-btn-sm" disabled={beschaeftigt || geaendert}
                        aria-label={`Vorschlag ${nr + 1} aufnehmen`} onClick={() => aufnehmen.mutate(nr)}>
                  hinzufügen
                </button>
              </div>
            ))}
          </section>
        )}

        {zeilen.some((z) => z.wahl) && (
          <div className="ang-hinweis" data-testid="angebot-alternativ-hinweis">
            Alternativen kommen in HERO als normale Position an, gekennzeichnet mit „ALTERNATIV“. Bitte dort als
            Alternativposition markieren – bis dahin rechnet HERO sie mit ({euro(alternativSumme)} mehr als hier).
          </div>
        )}

        <div className="ang-leiste">
          <div className="ang-summe" data-testid="angebot-summe">
            <span>Summe netto {euro(summe)}</span>
            {ohnePreis > 0 && <small style={{ color: "var(--warning-text)" }}> · {ohnePreis} ohne Preis</small>}
          </div>
          <button type="button" className="pp-btn" disabled={!geaendert || beschaeftigt} onClick={() => speichern.mutate()}>
            {speichern.isPending ? "Speichere …" : <><span className="ang-lang">Änderungen speichern</span><span className="ang-kurz">Speichern</span></>}
          </button>
          <button type="button" className="pp-btn pp-btn-primary" data-testid="angebot-nach-hero"
                  disabled={beschaeftigt || geaendert || !e.project_match_id || ohnePreis > 0}
                  onClick={() => hero.mutate()}>
            {hero.isPending ? "Lege in HERO an …" : <><span className="ang-lang">{e.status === "in_hero" ? "Erneut in HERO übernehmen" : "In HERO übernehmen"}</span><span className="ang-kurz">{e.status === "in_hero" ? "Erneut nach HERO" : "Nach HERO"}</span></>}
          </button>
          <div className="ang-leiste-status">
            {!e.project_match_id ? "Zuerst ein HERO-Projekt zuordnen." :
              ohnePreis > 0 ? `${ohnePreis} Position(en) brauchen noch einen Preis.` :
              geaendert ? "Erst speichern." :
              e.status === "in_hero" ? "Liegt als Entwurf in HERO. Erneut übernehmen ersetzt den HERO-Entwurf." :
              "Legt das Angebot als Entwurf im Projekt an. Prüfen und versenden in HERO."}
            {e.status === "in_hero" && e.hero_link && <> · <a href={e.hero_link} target="_blank" rel="noreferrer" data-frei>Projekt in HERO öffnen ↗</a></>}
          </div>
        </div>

        <div className="ang-fuss">
          <button type="button" className="pp-btn pp-btn-sm pp-btn-danger" disabled={weg.isPending}
                  onClick={() => { if (window.confirm("Entwurf im Portal löschen? HERO bleibt unberührt.")) weg.mutate() }}>
            Entwurf löschen
          </button>
        </div>
      </div>
    </>
  )
}
